import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2, Check, MessageSquare, FileText, Image, Trash2 } from 'lucide-react';

function SMSModal({ isOpen, onClose, phoneNumber, personName, personId, apiService, senderName }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  const formatDisplayPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length >= 10) {
      const last10 = digits.slice(-10);
      return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
    }
    return phone;
  };

  useEffect(() => {
    if (isOpen && personId) {
      fetchConversation();
    }
    if (isOpen) {
      setMessage('');
      setSent(false);
      setError(null);
      setImagePreview(null);
      setImageUrl(null);
    }
  }, [isOpen, personId]);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen, loadingConversation]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  const fetchConversation = async () => {
    setLoadingConversation(true);
    try {
      const data = await apiService.get(`/amn/messaging/conversation/${personId}/`);
      setConversation(data.messages || []);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    } finally {
      setLoadingConversation(false);
    }
  };

  const uploadImage = async (file) => {
    setUploading(true);
    setError(null);
    try {
      const preview = URL.createObjectURL(file);
      setImagePreview(preview);
      const formData = new FormData();
      formData.append('image', file);
      const resp = await apiService.post('/amn/messaging/upload-image/', formData, true);
      setImageUrl(resp.url);
    } catch (err) {
      setError('Failed to upload image');
      setImagePreview(null);
      setImageUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) uploadImage(file);
        return;
      }
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setImageUrl(null);
  };

  const handleSend = async () => {
    if ((!message.trim() && !imageUrl) || sending) return;
    setError(null);
    setSending(true);

    try {
      const payload = { message: message.trim() || (imageUrl ? 'Image attached' : '') };
      if (personId) payload.person_id = personId;
      else if (phoneNumber) payload.to_number = phoneNumber;
      if (imageUrl) payload.media_urls = [imageUrl];

      await apiService.post('/amn/messaging/send/', payload);
      setSent(true);
      setMessage('');
      setImagePreview(null);
      setImageUrl(null);

      if (personId) {
        await fetchConversation();
      }

      setTimeout(() => setSent(false), 2000);
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();

    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (isToday) return time;
    if (isYesterday) return `Yesterday ${time}`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + time;
  };

  const name = senderName || 'Jade';
  const greeting = new Date().getHours() < 12 ? 'Good morning' : 'Good afternoon';
  const TEMPLATES = [
    {
      label: 'Intro',
      text: `${greeting},\n\nThank you for your interest in our mobile nail care services. I am happy to help with questions you have and help you with scheduling.\n\nMay I ask what service you are looking for and the city or zip code for service?\n\nThank you, and I look forward to speaking with you!\n${name}\nAmour Mobile Nails`,
    },
  ];

  if (!isOpen) return null;

  const charCount = message.length;
  const segments = Math.ceil(charCount / 160) || 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 flex flex-col" style={{ maxHeight: '80vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {personName || 'Send SMS'}
              </p>
              <p className="text-xs text-gray-500">{formatDisplayPhone(phoneNumber)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Conversation History */}
        {personId && (
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-0" style={{ maxHeight: '40vh' }}>
            {loadingConversation ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : conversation.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">No messages yet</p>
            ) : (
              conversation.map((msg) => {
                const isOutgoing = msg.direction === 'outgoing';
                return (
                  <div key={msg.id} className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
                      isOutgoing
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-[10px] mt-1 ${isOutgoing ? 'text-blue-200' : 'text-gray-400'}`}>
                        {formatTime(msg.date_time)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Compose Area */}
        <div className="border-t border-gray-200 px-4 py-3">
          {error && (
            <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}
          <div className="flex gap-1.5 mb-2">
            {TEMPLATES.map((t, idx) => (
              <button
                key={idx}
                onClick={() => { setMessage(t.text); if (textareaRef.current) textareaRef.current.focus(); }}
                className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors"
                title={t.text}
              >
                <FileText className="w-3 h-3" />
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              {imagePreview && (
                <div className="relative inline-block mb-2">
                  <img src={imagePreview} alt="Attached" className="max-h-24 rounded-lg border border-gray-200" />
                  {uploading && (
                    <div className="absolute inset-0 bg-white/70 rounded-lg flex items-center justify-center">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                    </div>
                  )}
                  <button onClick={removeImage} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder="Type your message... (paste an image to attach)"
                rows={2}
                maxLength={1600}
                className="w-full resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleSend}
              disabled={!message.trim() || sending}
              className={`self-end px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 ${
                !message.trim() || sending
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : sent
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : sent ? (
                <Check className="w-4 h-4" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
          <div className="flex justify-between mt-1">
            <p className="text-[10px] text-gray-400">
              Enter to send, Shift+Enter for new line
            </p>
            <p className={`text-[10px] ${charCount > 160 ? 'text-amber-500' : 'text-gray-400'}`}>
              {charCount}/160{segments > 1 ? ` (${segments} segments)` : ''}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SMSModal;
